import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import {
  DEFAULT_MEDIA_DOC_META,
  defaultCategory,
  mediaDocErrorMessage,
  mediaDocumentsApi,
  notifyMediaDocumentsChanged,
} from '../../../lib/mediaDocuments';
import { useMediaDocuments } from '../../../lib/useMediaDocuments';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { FileBadge } from '../tabs/medien/shared';
import { type UploadRow } from './types';
import { WidgetCard } from './WidgetCard';

/* ─── DokumenteWidget (compact) ───────────────────────────────────────────── */

export function DokumenteWidget({ dark, userId, onShowAll }: {
  dark: boolean;
  userId: number;
  /** @deprecated v1-Liste — das Widget lädt selbst über /v2 (presigned URLs). */
  initialDocuments?: UploadRow[];
  onShowAll: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('users.write');
  const { data } = useMediaDocuments(userId);
  const documents = data?.documents ?? [];
  const meta = data?.meta ?? DEFAULT_MEDIA_DOC_META;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true); setUploadError(null);
    try {
      await mediaDocumentsApi.upload(userId, 'document', files, defaultCategory(files[0], 'document', meta));
      notifyMediaDocumentsChanged(userId);
    } catch (err) {
      setUploadError(mediaDocErrorMessage(err) ?? t('pflege.detail.dashboard.upload_error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [userId, meta, t]);

  const preview = documents.slice(0, 3);
  return (
    <WidgetCard dark={dark} title={t('pflege.detail.dashboard.dokumente')} badge={documents.length || null} onShowAll={onShowAll} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
      <div>
        {preview.length === 0 && <div style={{ padding: '10px 14px', fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>{t('pflege.media.empty_docs')}</div>}
        {preview.map(doc => (
          <a
            key={doc.id}
            href={doc.url ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            title={doc.name}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: `1px solid ${tc.borderSoft}`, textDecoration: 'none', color: tc.text }}
          >
            <FileBadge name={doc.name} size={26} />
            <span style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: tc.heading }}>{doc.name}</span>
          </a>
        ))}
      </div>
      {canWrite && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={meta.accept.documents.map(x => `.${x}`).join(',')}
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={handleFileChange}
          />
          {uploadError && <div style={{ margin: '4px 14px 0', fontSize: 11, color: '#b42318' }}>{uploadError}</div>}
          <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={uploading} style={{ margin: '8px 14px 10px', fontSize: 12, fontWeight: 600, color: BRAND, background: BRAND_LIGHT, border: `1px dashed ${BRAND}`, borderRadius: 7, padding: '5px 10px', cursor: uploading ? 'wait' : 'pointer', textAlign: 'left', opacity: uploading ? 0.7 : 1 }}>
            {uploading ? '…' : `+ ${t('pflege.detail.dashboard.dokument_hinzufuegen')}`}
          </button>
        </>
      )}
    </WidgetCard>
  );
}
